import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { endpoints, baseUrl } from '../../api/api';
import AdminHeader from './components/AdminHeader';

// ── Types ────────────────────────────────────────────────────────────────────

interface ColdSignup {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  createdAt: string;
  daysSinceSignup: number;
}

interface StalledDraft {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  bookId: string;
  bookTitle: string;
  daysSinceUpdate: number;
}

interface PostApprovalSilent {
  _id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  approvedBookTitle: string;
  approvedBookId: string;
  daysSinceApproval: number | null;
}

interface ReEngagementData {
  coldSignups: ColdSignup[];
  stalledDrafts: StalledDraft[];
  postApprovalSilent: PostApprovalSilent[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fullName(u: { firstName: string; lastName: string }) {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || '—';
}

function CopyButton({ value, label }: { value?: string; label: string }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span className="text-gray-300 text-xs">—</span>;
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      title={`Copy ${label}`}
      className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 group"
    >
      <span className="max-w-[160px] truncate">{value}</span>
      <svg className={`w-3 h-3 shrink-0 ${copied ? 'text-green-500' : 'text-gray-400 group-hover:text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        {copied
          ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 10h6a2 2 0 002-2v-8a2 2 0 00-2-2h-6a2 2 0 00-2 2v8a2 2 0 002 2z" />
        }
      </svg>
    </button>
  );
}

function DaysBadge({ days }: { days: number }) {
  const color = days > 30 ? 'bg-red-100 text-red-700' : days > 14 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600';
  return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${color}`}>{days}d ago</span>;
}

function SectionHeader({ title, description, count, color }: {
  title: string; description: string; count: number; color: string;
}) {
  return (
    <div className="flex items-start justify-between">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${color}`}>{count}</span>
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────────

const ColdSignupsSection: React.FC<{ users: ColdSignup[] }> = ({ users }) => (
  <div className="bg-white rounded-lg shadow">
    <div className="px-5 py-4 border-b border-gray-100">
      <SectionHeader
        title="Cold Signups"
        description="Signed up more than 7 days ago — profile still incomplete"
        count={users.length}
        color="bg-amber-100 text-amber-700"
      />
    </div>
    {users.length === 0 ? (
      <p className="px-5 py-8 text-sm text-gray-400 text-center">No cold signups right now</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Signed Up</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => (
              <tr key={u._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{fullName(u)}</td>
                <td className="px-4 py-3"><CopyButton value={u.email} label="email" /></td>
                <td className="px-4 py-3"><CopyButton value={u.phoneNumber} label="phone" /></td>
                <td className="px-4 py-3"><DaysBadge days={u.daysSinceSignup} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const StalledDraftsSection: React.FC<{ users: StalledDraft[]; onViewBook: (id: string) => void }> = ({ users, onViewBook }) => (
  <div className="bg-white rounded-lg shadow">
    <div className="px-5 py-4 border-b border-gray-100">
      <SectionHeader
        title="Stalled Drafts"
        description="Have a draft book that hasn't been touched in more than 14 days"
        count={users.length}
        color="bg-orange-100 text-orange-700"
      />
    </div>
    {users.length === 0 ? (
      <p className="px-5 py-8 text-sm text-gray-400 text-center">No stalled drafts right now</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Stalled Book</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Last Active</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => (
              <tr key={u._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{fullName(u)}</td>
                <td className="px-4 py-3"><CopyButton value={u.email} label="email" /></td>
                <td className="px-4 py-3"><CopyButton value={u.phoneNumber} label="phone" /></td>
                <td className="px-4 py-3 text-sm text-gray-700 max-w-[180px] truncate" title={u.bookTitle}>{u.bookTitle}</td>
                <td className="px-4 py-3"><DaysBadge days={u.daysSinceUpdate} /></td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onViewBook(u.bookId)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View book
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const PostApprovalSection: React.FC<{ users: PostApprovalSilent[]; onViewBook: (id: string) => void }> = ({ users, onViewBook }) => (
  <div className="bg-white rounded-lg shadow">
    <div className="px-5 py-4 border-b border-gray-100">
      <SectionHeader
        title="Post-Approval Silent"
        description="Got a book approved 30+ days ago — no new book activity since"
        count={users.length}
        color="bg-blue-100 text-blue-700"
      />
    </div>
    {users.length === 0 ? (
      <p className="px-5 py-8 text-sm text-gray-400 text-center">No post-approval silent users right now</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Approved Book</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Approval</th>
              <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map(u => (
              <tr key={u._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium text-gray-900">{fullName(u)}</td>
                <td className="px-4 py-3"><CopyButton value={u.email} label="email" /></td>
                <td className="px-4 py-3"><CopyButton value={u.phoneNumber} label="phone" /></td>
                <td className="px-4 py-3 text-sm text-gray-700 max-w-[180px] truncate" title={u.approvedBookTitle}>{u.approvedBookTitle}</td>
                <td className="px-4 py-3">{u.daysSinceApproval != null ? <DaysBadge days={u.daysSinceApproval} /> : '—'}</td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => onViewBook(u.approvedBookId)}
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    View book
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const OutreachDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData]       = useState<ReEngagementData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const token = localStorage.getItem('accessToken');
      const res   = await axios.get(`${baseUrl}${endpoints.adminGetReEngagement}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData.role !== 'admin') { navigate('/'); return; }
      } catch { navigate('/'); return; }
    }
    fetchData();
  }, [fetchData, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const totalFlagged = data
    ? data.coldSignups.length + data.stalledDrafts.length + data.postApprovalSilent.length
    : 0;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} />

      <main className="px-4 py-8 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Outreach</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Users who need a nudge — {loading ? '…' : `${totalFlagged} flagged across 3 cohorts`}
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1.5"
          >
            <svg className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900" />
          </div>
        )}

        {error && !loading && (
          <div className="bg-white rounded-lg shadow px-6 py-10 text-center">
            <p className="text-sm text-red-500">Failed to load outreach data.</p>
            <button onClick={fetchData} className="mt-3 text-sm text-blue-600 hover:underline">Try again</button>
          </div>
        )}

        {data && !loading && (
          <>
            <ColdSignupsSection users={data.coldSignups} />
            <StalledDraftsSection
              users={data.stalledDrafts}
              onViewBook={id => navigate(`/admin/book/${id}`)}
            />
            <PostApprovalSection
              users={data.postApprovalSilent}
              onViewBook={id => navigate(`/admin/book/${id}`)}
            />
          </>
        )}
      </main>
    </div>
  );
};

export default OutreachDashboard;